import { toast } from "sonner";
import { Truck, PackageCheck, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { api, yen, errMsg } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { DeliveryNotice } from "./DeliveryNotice";
import { COUNTRY_CODES } from "../../lib/wa";

/** Formulir data pemesan, zona antar, promo, dan tombol kirim pesanan. */
export const CheckoutForm = ({
  cust,
  setCust,
  fulfillment,
  setFulfillment,
  zone,
  setZone,
  city,
  setCity,
  settings,
  setLoyalty,
  promo,
  setPromo,
  totals,
  locating,
  shareLocation,
  busy,
  submit,
  itemCount,
  bentoOnly,
}) => {
  const { t } = useI18n();
  const cities = settings?.outside_cities || [];
  const { subtotal, minOrder, belowMin } = totals;

  const lookupLoyalty = async () => {
    const phone = cust.phone.trim();
    if (!phone) return setLoyalty(null);
    try {
      const { data } = await api.get("/customers/lookup", { params: { phone } });
      setLoyalty(data.found && data.discount_pct > 0 ? data : null);
      if (data.discount_pct > 0) toast.success(`${t("loyalty_welcome")} ${data.discount_pct}%`);
    } catch {
      setLoyalty(null);
    }
  };

  const applyPromo = async () => {
    try {
      const { data } = await api.get("/promos/validate", {
        params: { code: promo.code.trim(), subtotal },
      });
      setPromo({ code: data.code, discount: data.discount });
      toast.success(t("promo_ok"));
    } catch (e) {
      setPromo({ code: promo.code, discount: 0 });
      toast.error(errMsg(e, "Kode promo tidak valid"));
    }
  };

  return (
    <div className="space-y-3">
      <p className="font-display font-semibold text-sm">{t("checkout")}</p>
      <div className="grid grid-cols-2 gap-2">
        {[
          ["pickup", PackageCheck],
          ["delivery", Truck],
        ].map(([f, Icon]) => (
          <button
            key={f}
            data-testid={`fulfillment-${f}`}
            disabled={f === "delivery" && bentoOnly}
            onClick={() => setFulfillment(f)}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-colors duration-200 ${
              fulfillment === f
                ? "bg-primary text-white border-transparent"
                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            } ${f === "delivery" && bentoOnly ? "opacity-40 cursor-not-allowed hover:bg-white" : ""}`}
          >
            <Icon size={16} /> {t(f)}
          </button>
        ))}
      </div>
      {bentoOnly && (
        <p
          data-testid="bento-pickup-only-note"
          className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2"
        >
          {t("bento_pickup_only")}
        </p>
      )}
      <DeliveryNotice
        fulfillment={fulfillment}
        zone={zone}
        cutoffHour={settings?.delivery_cutoff_hour ?? 15}
        insideHour={settings?.delivery_inside_hour ?? 21}
      />
      <Input
        data-testid="cust-name-input"
        placeholder={t("cust_name")}
        value={cust.name}
        onChange={(e) => setCust({ ...cust, name: e.target.value })}
      />
      <Input
        data-testid="cust-phone-input"
        placeholder={t("cust_phone")}
        value={cust.phone}
        onChange={(e) => setCust({ ...cust, phone: e.target.value })}
        onBlur={lookupLoyalty}
      />
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            data-testid="wa-same-checkbox"
            checked={cust.whatsapp_same !== false}
            onChange={(e) => setCust({ ...cust, whatsapp_same: e.target.checked })}
            className="h-4 w-4 accent-emerald-600"
          />
          {t("wa_same_as_phone")}
        </label>
        {cust.whatsapp_same === false && (
          <div className="flex gap-2">
            <select
              data-testid="wa-cc-select"
              value={cust.whatsapp_cc || "81"}
              onChange={(e) => setCust({ ...cust, whatsapp_cc: e.target.value })}
              className="h-10 rounded-xl border border-gray-200 bg-white px-2 text-sm"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <Input
              data-testid="cust-whatsapp-input"
              placeholder={t("cust_whatsapp")}
              value={cust.whatsapp}
              onChange={(e) => setCust({ ...cust, whatsapp: e.target.value })}
            />
          </div>
        )}
      </div>
      <Input
        data-testid="cust-email-input"
        type="email"
        placeholder={t("cust_email")}
        value={cust.email}
        onChange={(e) => setCust({ ...cust, email: e.target.value })}
      />
      {fulfillment === "delivery" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {[
              ["inside", settings?.delivery_area || "Kumamoto shi"],
              ["outside", settings?.outside_area_label || t("zone_outside")],
            ].map(([z, label]) => (
              <button
                key={z}
                data-testid={`zone-${z}`}
                onClick={() => setZone(z)}
                className={`py-2 rounded-xl text-sm font-medium border transition-colors duration-200 ${
                  zone === z
                    ? "bg-gray-900 text-white border-transparent"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {zone === "outside" && (
            <div className="space-y-1">
              <select
                data-testid="city-select"
                className="w-full h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              >
                <option value="">{t("city_other")}</option>
                {cities.map((c) => (
                  <option key={c.city} value={c.city} data-testid={`city-option-${c.city}`}>
                    {`${c.city} · ¥${Number(c.fee).toLocaleString()}`}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">{t("city_hint")}</p>
            </div>
          )}
          <Input
            data-testid="cust-address-input"
            placeholder={t("cust_address")}
            value={cust.address}
            onChange={(e) => setCust({ ...cust, address: e.target.value })}
          />
          <Button
            variant="outline"
            className="w-full"
            data-testid="share-location-btn"
            disabled={locating}
            onClick={shareLocation}
          >
            <MapPin size={16} className="mr-2" />
            {cust.lat ? t("location_shared") : t("share_location")}
          </Button>
          {cust.lat && (
            <p className="text-xs text-muted-foreground num" data-testid="location-coords">
              {cust.lat.toFixed(5)}, {cust.lng.toFixed(5)}
            </p>
          )}
        </>
      )}
      <Textarea
        data-testid="cust-note-input"
        placeholder={t("cust_note")}
        value={cust.note}
        onChange={(e) => setCust({ ...cust, note: e.target.value })}
        rows={2}
      />
      {belowMin && (
        <p className="text-sm text-primary" data-testid="min-order-warning">
          {t("min_order_warn")}: {yen(minOrder)}
        </p>
      )}
      <div className="flex gap-2">
        <Input
          data-testid="promo-code-input-cart"
          placeholder={t("promo_apply")}
          value={promo.code}
          onChange={(e) => setPromo({ code: e.target.value.toUpperCase(), discount: 0 })}
        />
        <Button
          variant="outline"
          data-testid="apply-promo-btn"
          disabled={!promo.code.trim()}
          onClick={applyPromo}
        >
          {t("save")}
        </Button>
      </div>
      <Button
        className="w-full h-12"
        data-testid="place-order-btn"
        disabled={busy || itemCount === 0 || !cust.name.trim() || belowMin}
        onClick={submit}
      >
        {t("place_order")}
      </Button>
    </div>
  );
};
